import React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { Link } from 'core/routing';
import { Avatar, Username } from 'apps/components/shared';
import { CardIndicator } from 'legacyComponents/cards/types/indicator';
import {
  PostCard,
  TextWrapper,
  FullWidthTitle as Title,
  PostCardActions,
  pseudoCardStyles,
} from 'legacyComponents/cards/styles';
import { Date } from 'apps/components/date';

import { conf as mainConf } from 'apps/config.yaml';
import { clientStore } from 'apps/client/app';
import { imageSrc } from 'apps/file/utils';

import { withNamespaces, Trans } from 'react-i18next';

import { Button, Tooltip, Paragraph } from 'components';

export const ContributionCard = withNamespaces()(
  observer(({ cardObject, cardNo, cardColumns }) => {
    const noMargin = (cardNo + 1) % cardColumns === 0;
    if (cardObject === null) {
      return <CardIndicator noMargin={noMargin} type='issue' />;
    }

    const {
      title,
      creatorProfile,
      version,
      lastUpdated,
      zipArchiveUrl,
      content,
    } = cardObject;
    const versionRoute = `${clientStore.spaceRoute.route}.version.files`;
    const contribRoute = `${clientStore.spaceRoute.route}.contribution`;
    const shortVersion = version.substring(0, 7);
    const params = Object.assign({}, clientStore.spaceParams, {
      contributionId: shortVersion,
    });

    return (
      <PostCard data-intercom-target={cardNo === 0 ? 'project-history' : ''}>
        <Link
          name='profile'
          params={{ profile: creatorProfile.username }}
          content={creatorProfile.content}
        >
          <Avatar
            src={imageSrc(
              creatorProfile.avatar,
              creatorProfile.imageFallbackPath,
              mainConf.imgSizes.profile.small,
            )}
          />
        </Link>
        <TextWrapper>
          <Link name={contribRoute} params={params} content={content}>
            <Title>{title || `Contribution #${shortVersion}`}</Title>
          </Link>
          <Paragraph size='small'>
            <LinkContribution name={contribRoute} params={params}>
              #{shortVersion}
            </LinkContribution>
            <Trans>contributed by </Trans>
            <Username
              name='profile'
              params={{ profile: creatorProfile.username }}
              content={creatorProfile.content}
            >
              {creatorProfile.username}
            </Username>
            <DateStyled value={lastUpdated} />
          </Paragraph>
        </TextWrapper>
        <PostCardActions>
          <Tooltip delayShow={500} />
          <Link name={versionRoute} params={params} content={content}>
            <Button
              variant='secondary'
              icon='time_travel'
              ariaLabel='Files'
              data-tip='Browse the Drive at this point in history'
              outline
            />
          </Link>
          <Button
            variant='secondary'
            icon='download'
            ariaLabel='Download'
            as='a'
            target='_blank'
            href={zipArchiveUrl}
            rel='noopener noreferrer'
            data-tip='Download the Drive at this point in history'
            download
            outline
          />
        </PostCardActions>
      </PostCard>
    );
  }),
);

const LinkContribution = styled(Link)`
  color: ${props => props.theme.color.wfGrey700};
  text-decoration: underline;
  &:hover {
    text-decoration: none;
  }
  &::after {
    ${pseudoCardStyles}
  }
`;

const DateStyled = styled(Date)`
  &::before {
    ${pseudoCardStyles}
  }
`;

ContributionCard.propTypes = {
  avatar: PropTypes.string,
  title: PropTypes.string,
  id: PropTypes.string,
  author: PropTypes.string,
  datePublished: PropTypes.string,
  comments: PropTypes.array,
  imageFallbackPath: PropTypes.string,
};
